import type * as React from 'react';
import { useLayoutEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { maskSocialSecurityNumberChange } from './mask-social-security-number';

/** `inputType` the browser reports for a backspace. */
const DELETE_BACKWARD_INPUT_TYPE = 'deleteContentBackward';

type Props = Omit<React.ComponentProps<'input'>, 'onChange' | 'value'> & {
  /** Optional so a form typing the field as optional can spread its `field` as-is. */
  value: string | undefined;
  /** Called with the masked value — react-hook-form's `field.onChange` fits as-is. */
  onChange: (value: string) => void;
};

/**
 * Text input displaying a French social security number grouped as
 * `1 23 45 67 890 123 45`.
 *
 * The masked string is what lands in form state; callers normalize it before
 * sending it to the API. The caret is restored after each edit, so the number
 * can be corrected in the middle and not only appended to.
 *
 * The soft keyboard is `text` rather than `numeric` because the Corsica
 * department codes `2A` / `2B` need the letters A and B.
 *
 * @param props.value - the masked value held in form state
 * @param props.onChange - receives the re-masked value on every edit
 * @example
 * <FormField name="socialSecurityNumber" render={({ field }) => (
 *   <SocialSecurityNumberInput {...field} />
 * )} />
 */
const SocialSecurityNumberInput = ({
  value = '',
  onChange,
  ...props
}: Props) => {
  const elementRef = useRef<HTMLInputElement | null>(null);
  const caretRef = useRef<number | null>(null);

  // The value is re-grouped as it is typed, so the caret has to be re-anchored
  // once React has committed the new string to the DOM
  useLayoutEffect(() => {
    const caretPosition = caretRef.current;
    caretRef.current = null;
    if (caretPosition === null || elementRef.current === null) {
      return;
    }
    elementRef.current.setSelectionRange(caretPosition, caretPosition);
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    elementRef.current = input;

    const masked = maskSocialSecurityNumberChange({
      editedValue: input.value,
      caretPosition: input.selectionStart ?? input.value.length,
      previousValue: value,
      isBackspace:
        (event.nativeEvent as InputEvent).inputType ===
        DELETE_BACKWARD_INPUT_TYPE,
    });

    caretRef.current = masked.caretPosition;
    onChange(masked.value);
  };

  return (
    <Input
      autoComplete="off"
      inputMode="text"
      onChange={handleChange}
      value={value}
      {...props}
    />
  );
};

export default SocialSecurityNumberInput;
